import {Injectable} from '@angular/core';
import {ApiClientService} from '../api-client/api-client.service';
import {forkJoin, map, Observable, Subscriber} from 'rxjs';
import {ApiResponse} from '../../api/api-response';
import {Paginator} from "../../tools/pagination/paginator";
import {TableSorter} from "../../tools/table-sorter/table-sorter";
import {Partner} from '../../models/partner/partner';
import {PartnerStat} from '../../models/statistic/partner-stat';
import {PartnerTypesStat} from '../../models/statistic/partner-types-stat';
import {ProductStat} from '../../models/statistic/product-stat';
import {SiteStat} from '../../models/statistic/site-stat';
import {TopPartnerStat} from '../../models/statistic/top-partner-stat';
import {DetailedStatisticsParts, mergeDetailedStatistics} from './detailed-statistics-merger';

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {

  constructor(private apiClient: ApiClientService) {
  }

  getDetailed(params: any): Observable<any> {
    return forkJoin(DetailedStatisticsParts.map(parts => this.getDetailedPart({...params, parts}))).pipe(
      map(responses => mergeDetailedStatistics(responses))
    );
  }

  private getDetailedPart(params: any): Observable<any> {
    return new Observable<any>(subscriber => {
      this.apiClient.get('/statistics/detailed', params).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData());
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getFunnels(params: any): Observable<any> {
    return new Observable<any>(subscriber => {
      this.apiClient.get('/statistics/funnels', params).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData());
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getROI(params: any): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      this.apiClient.get('/statistics/roi', params).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData());
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getConversions(
    params: any,
    paginator: Paginator | null = null,
    tableSorter: TableSorter | null = null
  ): Observable<any[]> {
    return new Observable<any[]>(subscriber => {
      if (paginator) {
        params.page = paginator.currentPage;
        params.count = paginator.countByPage;
      }

      if (tableSorter) {
        params.sort = tableSorter.sortName;
        params.order = tableSorter.sortOrder;
      }

      this.apiClient.get('/statistics/conversion', params).subscribe({
        next: (response: ApiResponse) => {

          if (paginator) {
            paginator.count = response.getTotal();
          }

          subscriber.next(response.getData());
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getHomePartners(): Observable<PartnerTypesStat> {
    return new Observable<PartnerTypesStat>((subscriber: Subscriber<PartnerTypesStat>) => {
      this.apiClient.get('/statistics/home/partners').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(new PartnerTypesStat().deserialize(response.getData()));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getHomeProducts(): Observable<ProductStat[]> {
    return new Observable<ProductStat[]>((subscriber: Subscriber<ProductStat[]>) => {
      this.apiClient.get('/statistics/home/products').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData().map((product: any) => new ProductStat().deserialize(product)));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getHomeSites(): Observable<SiteStat[]> {
    return new Observable<SiteStat[]>((subscriber: Subscriber<SiteStat[]>) => {
      this.apiClient.get('/statistics/home/sites').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData().map((site: any) => new SiteStat().deserialize(site)));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getHomeTopAffiliates(): Observable<TopPartnerStat[]> {
    return new Observable<TopPartnerStat[]>((subscriber: Subscriber<TopPartnerStat[]>) => {
      this.apiClient.get('/statistics/home/top-affiliates').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData().map((partner: any) => new TopPartnerStat().deserialize(partner)));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getHomeTopMediabuyers(): Observable<TopPartnerStat[]> {
    return new Observable<TopPartnerStat[]>((subscriber: Subscriber<TopPartnerStat[]>) => {
      this.apiClient.get('/statistics/home/top-mediabuyers').subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(response.getData().map((partner: any) => new TopPartnerStat().deserialize(partner)));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }

  getActivity(partner: Partner | null = null): Observable<PartnerStat> {
    return new Observable<PartnerStat>((subscriber: Subscriber<PartnerStat>) => {
      const parameters = partner ? {partnerId: partner.id} : {};
      this.apiClient.get('/statistics/activity', parameters).subscribe({
        next: (response: ApiResponse) => {
          subscriber.next(new PartnerStat().deserialize(response.getData()));
          subscriber.complete();
        },
        error: (error) => {
          subscriber.error(error);
          subscriber.complete();
        }
      });
    });
  }
}
